import { Key, useRef } from 'react';

import { TreeRoot } from './tree-node';
import { TreeSection } from './tree-section';
import { expandParentsOfItemsMatchingSearch, getTreeNodeDefaultsFromChildren } from './tree-utils';
import { TreeData, TreeDataNode, TreeProps } from './tree.types';
import { mergeStyles } from '../..';

export { removeRedundantKeys } from './tree-utils';

const rootClasses = 'tree bg-white';

export const getTreeData = (
  data: TreeData[],
  checkedKeys: React.MutableRefObject<Set<Key>>,
  expandedKeys: React.MutableRefObject<Set<Key>>,
  searchTerm?: string
) => {
  function getTreeDataRecursive(data: TreeData[], level: number, parentId?: Key): TreeDataNode[] {
    return data.map((node: TreeData, index: number) => {
      const id = node.id ?? `${level}+${index}`;
      const nextLevel = node.isSection ? 0 : level + 1;

      expandParentsOfItemsMatchingSearch(id, node, expandedKeys, parentId, searchTerm);

      const children = node.children ? getTreeDataRecursive(node.children, nextLevel, id) : undefined;

      getTreeNodeDefaultsFromChildren(id, checkedKeys, expandedKeys, children);

      return {
        ...node,
        id,
        isChecked: checkedKeys.current.has(id),
        isExpanded: expandedKeys.current.has(id),
        children: children,
      } as TreeDataNode;
    });
  }
  return getTreeDataRecursive(data, 0);
};

export const Tree = (props: TreeProps) => {
  const { treeData, className, defaultCheckedKeys, defaultExpandedKeys, onCheck, onExpand, searchTerm = '' } = props;

  // These are refs because we don't need rerenders when these change. These are only used for callbacks
  const checkedKeys = useRef(new Set(props.checkedKeys ?? defaultCheckedKeys));
  const expandedKeys = useRef(new Set(defaultExpandedKeys));

  if (props.checkedKeys) {
    checkedKeys.current = props.checkedKeys;
  }

  // Add some properties to the tree data such as whether nodes are expanded
  // or checked by default. This also helps control revealing search results
  const data = getTreeData(treeData, checkedKeys, expandedKeys, searchTerm);
  return (
    <div className={mergeStyles([rootClasses, className])}>
      {data.map((node: TreeDataNode) => {
        if (node.isSection) {
          return (
            <TreeSection key={node.id} title={node.title}>
              {node.children?.map((node: TreeDataNode) => (
                <TreeRoot {...{ ...node, checkedKeys, expandedKeys, onCheck, onExpand, searchTerm }} key={node.id} />
              ))}
            </TreeSection>
          );
        }
        return <TreeRoot {...{ ...node, checkedKeys, expandedKeys, onCheck, onExpand, searchTerm }} key={node.id} />;
      })}
    </div>
  );
};

export default Tree;
